import React, {
  type FunctionComponent,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Button,
  Modal,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  NotificationLevel,
  notify,
  spacing,
  Text,
} from '@retailer-platform/shared-components'
import { IconButton } from '@instacart/ids-customers'
import { ConfirmIcon } from '@instacart/ids-core'
import faker from 'faker'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { startCase } from 'lodash'
import {
  AttributeManagementCategoryEnum,
  type AttributeManagementStepVersion,
  AttributeManagementTypeEnum,
  graphQLStepVersionToAttributeManagementStepVersion,
  type PipelineStep,
  type PipelineStepNextStepsRule,
  PipelineStepType,
} from '../../../types/attributeManagement.types'
import { useDomainMessages } from '../../../utils/domain/intl'
import AttributeManagementList from '../../attribute-management-list/AttributeManagementList'
import { useGetListStepVersionsQuery } from '../../../api'
import { ButtonRow } from '../../shared-styled-components/SharedStyledComponents'
import { PipelineFormStepVersionSelectTable } from './PipelineFormStepVersionSelectTable'
import StepAttributeForm from './StepAttributeForm'
import { PipelineFormStepSelectControlModal } from './PipelineFormStepSelectControlModal.tsx'

interface Props {
  category: AttributeManagementCategoryEnum | null
  onClose: () => void
  onConfirm: (step: PipelineStep) => void
  includedCategories?: AttributeManagementCategoryEnum[]
  step?: PipelineStep
}

interface State {
  category: AttributeManagementCategoryEnum | null
  selectedStepId: string | null
  selectedStepVersionId: string | null
  baseAttributes: any | null
  overrideAttributes: any | null
  type: AttributeManagementTypeEnum | null
  version: string | null
  nextStepsRule?: PipelineStepNextStepsRule | null
}

const DEFAULT_STATE: State = {
  category: null,
  selectedStepId: null,
  selectedStepVersionId: null,
  baseAttributes: null,
  overrideAttributes: null,
  type: null,
  version: null,
}

const ExplanationText = styled.div({
  marginBottom: spacing.X24,
})

const EXCLUDED_STEP_CATEGORIES = [
  AttributeManagementCategoryEnum.Unknown,
  AttributeManagementCategoryEnum.Input,
  AttributeManagementCategoryEnum.Control,
]

const PipelineFormStepSelectModal: React.FC<Props> = ({
  onClose,
  onConfirm,
  category,
  includedCategories,
  step,
}) => {
  const i18n = useDomainMessages({
    selectStepTitle:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepTitle',
    selectStepVersionTitle:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepVersionTitle',
    reset:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.reset',
    restart:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.restart',
    confirm:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.confirm',
    setOverridesTitle:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.setOverridesTitle',
    selectStepDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepDescription',
    selectStepVersionDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepVersionDescription',
    setOverridesDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.setOverridesDescription',
    cancel: 'catalogAdminDomain.attributeManagement.runTests.modal.button.cancel',
  })
  const [state, setState] = useState<State>({ ...DEFAULT_STATE, category })

  const isValidStep = useMemo(() => {
    const isValidNonControlStep =
      state.category != AttributeManagementCategoryEnum.Control &&
      state.baseAttributes &&
      state.overrideAttributes &&
      state.selectedStepVersionId &&
      state.version &&
      state.selectedStepId
    const isValidControlStep =
      state.category === AttributeManagementCategoryEnum.Control && !!state.nextStepsRule
    return isValidControlStep || isValidNonControlStep
  }, [
    state.baseAttributes,
    state.category,
    state.nextStepsRule,
    state.overrideAttributes,
    state.selectedStepId,
    state.selectedStepVersionId,
    state.version,
  ])

  const confirm = React.useCallback(() => {
    if (!isValidStep) return
    // Create an object with only the keys that have different values from baseAttributes
    const attributeOverrides = Object.keys(state?.overrideAttributes ?? {}).reduce((acc, key) => {
      // Only include keys where values differ
      if (
        JSON.stringify(state.overrideAttributes[key]) !== JSON.stringify(state.baseAttributes[key])
      ) {
        acc[key] = state.overrideAttributes[key]
      }
      return acc
    }, {} as Record<string, any>)

    const step: PipelineStep =
      state.category === AttributeManagementCategoryEnum.Control
        ? {
            type: PipelineStepType.Control,
            nextStepsRule: state.nextStepsRule,
          }
        : {
            type: state.type as string,
            stepVersionId: state?.selectedStepVersionId ?? '',
            stepId: state?.selectedStepId ?? '',
            version: state.version,
            attributeOverrides,
          }
    onConfirm(step)
  }, [
    isValidStep,
    state.overrideAttributes,
    state.category,
    state.type,
    state?.selectedStepVersionId,
    state?.selectedStepId,
    state.version,
    state.nextStepsRule,
    state.baseAttributes,
    onConfirm,
  ])

  return (
    <Modal
      isOpen={true}
      maxWidth={'90%'}
      maxHeight={'90%'}
      onRequestClose={onClose}
      styleOverrides={{
        content: {
          height:
            state?.category === AttributeManagementCategoryEnum.Control ? 'fit-content' : '90vh',
          width:
            state?.category === AttributeManagementCategoryEnum.Control ? 'fit-content' : '90vw',
        },
      }}
    >
      <ModalContainer>
        <ModalHeader>
          {(state.selectedStepVersionId && i18n.setOverridesTitle) ||
            (state.selectedStepId && i18n.selectStepVersionTitle) ||
            (state.category && i18n.selectStepTitle) ||
            'Select a category'}
        </ModalHeader>
        {!state.category && (
          <SelectCategoryModalContent
            includedCategories={includedCategories}
            onSelect={category => setState({ ...state, category })}
          />
        )}
        {state.category && !state.selectedStepId && (
          <StepSelectModalContent state={state} step={step} setState={state => setState(state)} />
        )}
        {state.selectedStepId && !state.selectedStepVersionId && (
          <StepVersionSelectModalContent
            stepId={state.selectedStepId}
            onSelect={(stepVersionId, attributes, type, version) => {
              const overrideAttributes = attributes
              setState({
                ...state,
                selectedStepVersionId: stepVersionId,
                overrideAttributes: overrideAttributes,
                baseAttributes: attributes,
                type,
                version,
              })
            }}
          />
        )}
        {state.selectedStepId && state.selectedStepVersionId && (
          <OverrideSelectionModalContent
            stepVersion={state.selectedStepVersionId}
            type={state.type || AttributeManagementTypeEnum.LLMPromptTemplate}
            attributes={state.overrideAttributes}
            onChange={attributes => setState({ ...state, overrideAttributes: attributes })}
          />
        )}
        <ModalFooter>
          <ButtonRow>
            <Button variant={'secondary'} onClick={onClose}>
              {i18n.cancel}
            </Button>
            <Button
              variant={'secondary'}
              disabled={!state.selectedStepId}
              onClick={() => setState(DEFAULT_STATE)}
            >
              {i18n.restart}
            </Button>
            <Button
              variant={'secondary'}
              disabled={
                !state.selectedStepVersionId ||
                JSON.stringify(state.overrideAttributes) === JSON.stringify(state.baseAttributes)
              }
              onClick={() => setState({ ...state, overrideAttributes: state.baseAttributes })}
            >
              {i18n.reset}
            </Button>
            <Button
              variant={'primary'}
              disabled={
                (!state.selectedStepVersionId &&
                  state.category != AttributeManagementCategoryEnum.Control) ||
                (!state.nextStepsRule && state.category === AttributeManagementCategoryEnum.Control)
              }
              onClick={confirm}
            >
              {i18n.confirm}
            </Button>
          </ButtonRow>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  )
}

interface OverrideSelectionProps {
  type: AttributeManagementTypeEnum
  stepVersion: string
  attributes: any
  onChange: (attributes: any) => void
}

const OverrideSelectionModalContent = ({
  stepVersion,
  type,
  attributes,
  onChange,
}: OverrideSelectionProps) => {
  const i18n = useDomainMessages({
    setOverridesDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.setOverridesDescription',
  })
  return (
    <ModalContent explanation={i18n.setOverridesDescription}>
      <StepAttributeForm
        type={type}
        attributes={attributes}
        mode={'override'}
        onChange={attributes => onChange(attributes)}
        version={parseInt(stepVersion)}
      />
    </ModalContent>
  )
}
interface StepVersionSelectProps {
  stepId: string
  onSelect: (
    stepVersionId: string,
    attributes: any,
    type: AttributeManagementTypeEnum,
    version: string
  ) => void
}

const StepVersionSelectModalContent = ({ stepId, onSelect }: StepVersionSelectProps) => {
  const i18n = useDomainMessages({
    selectStepVersionDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepVersionDescription',
  })
  const idempotencyKey = useRef(faker.random.uuid())
  const { data, loading } = useGetListStepVersionsQuery({
    variables: {
      clientId: 'catalog_camp_ipp',
      idempotencyKey: idempotencyKey.current,
      stepId: stepId,
    },
    onError: (error: any) => {
      notify({
        contents: `Error fetching steps: ${error.message}`,
        level: NotificationLevel.Error,
      })
    },
  })
  const versions: AttributeManagementStepVersion[] = useMemo(
    () =>
      data?.instacart_catalog_catflow_v1_CatflowAPI_ListStepVersions?.stepVersions
        ?.map(item => graphQLStepVersionToAttributeManagementStepVersion(item))
        ?.sort((a, b) => parseInt(b.version ?? '0') - parseInt(a.version ?? '0')) || [],
    [data]
  )
  return (
    <ModalContent explanation={i18n.selectStepVersionDescription}>
      <Text size="large" weight="semibold" styles={css({ textAlign: 'center' })}>
        {
          data?.instacart_catalog_catflow_v1_CatflowAPI_ListStepVersions?.stepVersions?.[0]?.step
            ?.name
        }
      </Text>
      <PipelineFormStepVersionSelectTable
        listData={versions}
        loading={loading}
        onSelect={stepVersionId =>
          onSelect(
            stepVersionId,
            versions.find(v => v.id === stepVersionId)?.attributes,
            AttributeManagementTypeEnum[
              (data?.instacart_catalog_catflow_v1_CatflowAPI_ListStepVersions?.stepVersions?.[0]
                ?.step?.type || 'LLMPromptTemplate') as keyof typeof AttributeManagementTypeEnum
            ],
            versions.find(v => v.id === stepVersionId)?.version || ''
          )
        }
      />
    </ModalContent>
  )
}
interface StepSelectProps {
  state: State
  step?: PipelineStep
  setState: (state: State) => void
}

const StepSelectModalContent = ({ state, setState, step }: StepSelectProps) => {
  const i18n = useDomainMessages({
    selectStepDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepDescription',
    selectStepTitle:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepTitle',
  })
  const action = React.useCallback(
    (stepId: string) => (
      <IconButton
        icon={ConfirmIcon}
        accessibleLabel={i18n.selectStepTitle}
        onPress={() => setState({ ...state, selectedStepId: stepId })}
      />
    ),
    [i18n.selectStepTitle, setState, state]
  )

  if (state?.category === AttributeManagementCategoryEnum.Control) {
    return (
      <ModalContent>
        <PipelineFormStepSelectControlModal
          step={{
            ...(step ?? {}),
            ...state,
          }}
          onChange={step =>
            setState({
              ...state,
              nextStepsRule: step.nextStepsRule,
            })
          }
        />
      </ModalContent>
    )
  }

  return (
    <ModalContent explanation={i18n.selectStepDescription}>
      <AttributeManagementList
        category={state?.category ?? AttributeManagementCategoryEnum.Unknown}
        hideCreateButton
        actions={[action]}
      />
    </ModalContent>
  )
}
interface CategorySelectProps {
  onSelect: (category: AttributeManagementCategoryEnum) => void
  includedCategories?: AttributeManagementCategoryEnum[]
}

const SelectCategoryModalContent = ({ onSelect, includedCategories }: CategorySelectProps) => {
  const categories = Object.values(AttributeManagementCategoryEnum).filter(
    category => !EXCLUDED_STEP_CATEGORIES.includes(category)
  )

  if (includedCategories) {
    for (const category of includedCategories) {
      categories.push(category as AttributeManagementCategoryEnum)
    }
  }

  return (
    <ModalContent explanation="Select a category of step to add">
      <div>
        {categories.map(category => (
          <Button key={category} variant={'secondary'} onClick={() => onSelect(category)}>
            {startCase(category.toLowerCase())}
          </Button>
        ))}
      </div>
    </ModalContent>
  )
}

interface ModalContentProps {
  explanation?: string
}

const ModalContent: FunctionComponent<PropsWithChildren<ModalContentProps>> = ({
  explanation,
  children,
}) => (
  <ModalBody>
    {explanation && (
      <ExplanationText>
        <Text size="small" weight="semibold">
          {explanation}
        </Text>
      </ExplanationText>
    )}
    {children}
  </ModalBody>
)
export default PipelineFormStepSelectModal
